import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Chip
} from '@mui/material';
import {
  CloudUpload,
  Inventory,
  ContentCut,
  LocalShipping,
  ArrowBack,
  CheckCircle,
  Info,
  GetApp,
  Publish,
  AttachMoney 
} from '@mui/icons-material';

const SoloDataManagement = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);

  // Custom Pound Icon Component
  const PoundIcon = ({ sx }) => (
    <Box sx={{ fontSize: 40, fontWeight: 'bold', ...sx }}>£</Box>
  );

  // Data types relevant to solo traders (NO EMPLOYEES)
  const dataTypes = [
      
      {
      id: 'income',
      title: 'Income',
      description: 'Import your daily takings by employee, including service and product sales',
      icon: <PoundIcon />,
      color: '#9C27B0',
      importRoute: '/import/income',
      exportRoute: '/export/income',
      features: [
      'Employee names & daily sales',
      'Service vs product split',
      'Client & product counts',
      'Hours worked & shift times'
      ],
      hasImport: true,
      hasExport: true
    },
    {
      id: 'products',
      title: 'Products',
      description: 'Import your product catalog with pricing, stock levels, and supplier information',
      icon: <Inventory sx={{ fontSize: 40 }} />,
      color: '#E91E63',
      importRoute: '/import/products',
      exportRoute: '/export/products',
      features: [
        'Product names, barcodes & descriptions',
        'Current & average cost prices',
        'Retail prices with VAT rates',
        'Stock levels & reorder points'
      ],
      hasImport: true,
      hasExport: true
    },
    {
      id: 'services',
      title: 'Services',
      description: 'Import your service menu with pricing, duration, and product requirements',
      icon: <ContentCut sx={{ fontSize: 40 }} />,
      color: '#2196F3',
      importRoute: '/import/services',
      exportRoute: '/export/services',
      features: [
        'Service names & descriptions',
        'Duration & pricing information',
        'Product usage & quantities',
        'Category organization'
      ],
      hasImport: true,
      hasExport: true
    },
    {
      id: 'expenditure',
      title: 'Business Expenses',
      description: 'Import historical expenses and business costs for tax calculations',
      icon: <PoundIcon />,
      color: '#FF9800',
      importRoute: '/import/expenditure',
      exportRoute: '/export/expenditure',
      features: [
        'Expense dates & amounts',
        'Category classifications',
        'Supplier & payment information',
        'VAT & receipt tracking'
      ],
      hasImport: true,
      hasExport: true
    },
    {
      id: 'suppliers',
      title: 'Suppliers',
      description: 'Import supplier contacts and terms for efficient stock ordering',
      icon: <LocalShipping sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
      importRoute: '/import/suppliers',
      exportRoute: '/export/suppliers',
      features: [
        'Supplier names & contact details',
        'Payment terms & delivery info',
        'Product associations',
        'Account numbers & references'
      ],
      hasImport: true,
      hasExport: true
    }
  ];

  const DataTypeCard = ({ dataType }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
          borderColor: dataType.color
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${dataType.color}15`,
              color: dataType.color
            }}
          >
            {dataType.icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {dataType.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              {dataType.hasImport && (
                <Chip label="Import" size="small" color="primary" variant="outlined" />
              )}
              {dataType.hasExport && (
                <Chip label="Export" size="small" color="success" variant="outlined" />
              )}
            </Box>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {dataType.description}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Includes:
        </Typography>
        <List dense sx={{ mb: 2 }}>
          {dataType.features.map((feature, index) => (
            <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckCircle sx={{ fontSize: 16, color: dataType.color }} />
              </ListItemIcon>
              <ListItemText
                primary={feature}
                primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }}
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
          {dataType.hasImport && (
            <Button
              variant="contained"
              fullWidth
              startIcon={<Publish />}
              onClick={() => navigate(dataType.importRoute)}
              sx={{ bgcolor: dataType.color, '&:hover': { bgcolor: dataType.color } }}
            >
              Import {dataType.title}
            </Button>
          )}
          {dataType.hasExport && (
            <Button
              variant="outlined"
              fullWidth
              startIcon={<GetApp />}
              onClick={() => navigate(dataType.exportRoute)}
              sx={{ borderColor: dataType.color, color: dataType.color }}
            >
              Export {dataType.title}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const OverviewTab = () => (
    <Box>
      {/* Welcome Section */}
      <Paper sx={{ p: 4, mb: 4, bgcolor: '#2196F3', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CloudUpload sx={{ fontSize: 48 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Import Your Business Data
            </Typography>
            <Typography variant="body1">
              Easily import your products, services, expenses, and suppliers for quick setup
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" color="primary" fontWeight="bold">
              5
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Data Types Available
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" color="success.main" fontWeight="bold">
              CSV
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Excel Compatible
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" color="warning.main" fontWeight="bold">
              ✓
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Validation Included
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" color="error.main" fontWeight="bold">
              UK
            </Typography>
            <Typography variant="body2" color="text.secondary">
              UK Format Support
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Data Type Cards */}
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        Available Data Types
      </Typography>
      <Grid container spacing={3}>
        {dataTypes.map((dataType) => (
          <Grid item xs={12} md={6} key={dataType.id}>
            <DataTypeCard dataType={dataType} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const GuidelinesTab = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Important Guidelines
        </Typography>
        <Typography variant="body2">
          Please read these guidelines carefully before importing data to ensure successful uploads
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="primary" />
              File Format Requirements
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="CSV or Excel (.xlsx) files"
                  secondary="Use UTF-8 encoding for special characters"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Maximum file size: 10MB"
                  secondary="For larger datasets, split into multiple files"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="First row must be headers"
                  secondary="Use the template column names exactly"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="No empty rows"
                  secondary="Remove any blank rows from your data"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="warning" />
              Data Format Guidelines
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Dates: DD/MM/YYYY"
                  secondary="e.g., 31/12/2024"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Currency: No symbols"
                  secondary="Use 10.50 not £10.50"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Phone: UK format"
                  secondary="07123456789 or 01234567890"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Barcodes: Standard format"
                  secondary="EAN-13 or UPC codes for products"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, bgcolor: 'warning.light' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              ⚠️ Before You Import
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  1. Download the Template
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Always use our template to ensure correct column headers and formatting
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  2. Check for Duplicates
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Remove duplicate entries - system will skip or update based on unique IDs
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  3. Backup First
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Export your current data before importing to have a backup
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  4. Test with Small File
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Import a few test records first to verify formatting is correct
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Simple Header - No Sidebar */}
      <Paper 
        elevation={0} 
        sx={{ 
          bgcolor: 'white', 
          borderBottom: '1px solid #e0e0e0',
          p: 2
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/solo_dashboard')}
                sx={{ textTransform: 'none' }}
              >
                Back to Dashboard
              </Button>
              <Divider orientation="vertical" flexItem />
              <CloudUpload sx={{ color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">
                Import Data
              </Typography>
            </Box>
          </Box>
        </Container>
      </Paper>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            variant="fullWidth"
          >
            <Tab label="Overview" />
            <Tab label="Import Guidelines" />
          </Tabs>
        </Paper>

        {currentTab === 0 && <OverviewTab />}
        {currentTab === 1 && <GuidelinesTab />}
      </Container>
    </Box>
  );
};

export default SoloDataManagement;